import React, { useState, useCallback } from 'react';
import { Table, Pagination, Button, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ data, permission, currentTheme }) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(''); // Nova state para coluna de ordenação
  const [orderDirection, setOrderDirection] = useState('asc'); // Nova state para direção da ordenação

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page ao mudar linhas por página
  }, []);

  // Função para manipular o clique no cabeçalho e ordenar
  const handleSort = (column) => {
    if (orderBy === column) {
      // Inverte a direção se a coluna já está selecionada
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Define uma nova coluna e inicia com ascendente
      setOrderBy(column);
      setOrderDirection('asc');
    }
    // Resetar para primeira página quando mudar ordenação
    setPage(0);
  };

  // Função para ordenar os dados com base na coluna e direção
  const sortData = (dataToSort) => {
    if (!orderBy) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let valueA, valueB;

      // Definir valores para comparação com base na coluna
      switch (orderBy) {
        case 'serial':
          valueA = a?.serial || '';
          valueB = b?.serial || '';
          break;
        case 'ip':
          valueA = a?.ip || '';
          valueB = b?.ip || '';
          break;
        case 'company_id':
          valueA = a?.company_id || '';
          valueB = b?.company_id || '';
          break;
        case 'company_name':
          valueA = a?.company?.name || '';
          valueB = b?.company?.name || '';
          break;
        case 'updatedAt':
          valueA = new Date(a?.updatedAt || 0);
          valueB = new Date(b?.updatedAt || 0);
          break;
        default:
          valueA = '';
          valueB = '';
      }

      // Comparação dos valores considerando a direção
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else if (valueA instanceof Date && valueB instanceof Date) {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        const comparison = String(valueA).localeCompare(String(valueB));
        return orderDirection === 'asc' ? comparison : -comparison;
      }
    });
  };

  // Renderizar ícone de ordenação usando react-icons
  const renderSortIcon = (column) => {
    if (orderBy !== column) {
      return <FaSort style={{ marginLeft: 5, opacity: 0.5 }} />;
    }
    return orderDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: 5, opacity: 1 }} />
      : <FaSortDown style={{ marginLeft: 5, opacity: 1 }} />;
  };

  // Aplicar ordenação antes da paginação
  const sortedData = sortData(data);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleClick = (_id) => {
    history.push(`/remote-access/${_id}`);
  };

  // Função para formatar datas
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Equipamentos
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th
                  className="text-center"
                  onClick={() => handleSort('serial')}
                  style={{ cursor: 'pointer' }}
                >
                  SERIAL {renderSortIcon('serial')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('ip')}
                  style={{ cursor: 'pointer' }}
                >
                  IP {renderSortIcon('ip')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('company_id')}
                  style={{ cursor: 'pointer' }}
                >
                  COMPANY ID {renderSortIcon('company_id')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('company_name')}
                  style={{ cursor: 'pointer' }}
                >
                  EMPRESA {renderSortIcon('company_name')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('updatedAt')}
                  style={{ cursor: 'pointer' }}
                >
                  ATUALIZADO EM {renderSortIcon('updatedAt')}
                </th>
                <th className="text-center">AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="clickable-row"
                  style={{ cursor: permission?.read ? 'pointer' : 'default' }}
                >
                  <td className="text-center pt-2">{item.serial || '--'}</td>
                  <td className="text-center pt-2">{item.ip || '--'}</td>
                  <td className="text-center pt-2">
                    {item?.company_id || '--'}
                  </td>
                  <td className="text-center pt-2">{item?.company?.name || '--'}</td>
                  <td className="text-center pt-2">{formatDate(item.updatedAt)}</td>
                  <td className="text-center">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(item?.serial)
                      }}
                      disabled={!permission?.write}
                    >
                      Conectar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"
            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"
            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
